/**
 * The Karr brand lockup and the standalone mark.
 *
 * Both ship as PNGs generated from `assets/brand/` by `npm run brand` — see
 * `scripts/gen-brand-assets.mjs`. Nothing here draws the logo in CSS: the old
 * gradient-tile "K" was a stand-in, and the real artwork is not reproducible in
 * markup.
 *
 * The tone swap is two <img>s toggled by the `dark:` variant, not one source
 * behind a media query — Karr's theme is a `.dark` class on <html> that the user
 * can set explicitly (see index.html + src/shared/utils/theme.ts), so
 * `prefers-color-scheme` would ignore a manual light-on-a-dark-OS choice.
 *
 * @since 1.0.0
 */
import { memo } from 'react';

/** The shipped lockup is 720x192. */
const LOCKUP_RATIO = 720 / 192;

/**
 * ⚠ Size this by the WORDMARK, not by the box. The mark is 90% of the lockup's
 * height but the KARR letters are only **29%** of it, so `height` buys far less
 * apparent size than it looks like it should: a 34px lockup renders a 9.9px cap
 * height — no bigger than the 14px nav links beside it, which is why the first
 * cut of this read as small and weak.
 *
 *     cap height ≈ height × 0.29        height ≈ desired cap ÷ 0.29
 *
 * The old text wordmark it replaced sat at a ~14px cap, which is a 48px lockup.
 * Anything under ~38px is only legible where nothing competes with it.
 *
 * Below ~40px of available bar, use {@link KarrMark} instead — see AppLayout,
 * whose header is `h-10`.
 */
const CAP_RATIO = 56 / 192;

export interface KarrLogoProps {
    /** Rendered height in px. Width follows the lockup's 3.75:1 ratio. */
    height?: number;
    className?: string;
}

/**
 * Full lockup: mark, rule, KARR wordmark. Use wherever the brand is named and
 * there is room for it — auth card, marketing nav and footer, growth navbars.
 */
export const KarrLogo = memo(function KarrLogo({ height = 44, className = '' }: KarrLogoProps) {
    if (import.meta.env.DEV && height * CAP_RATIO < 10) {
        console.warn(
            `<KarrLogo height={${height}}> renders a ${(height * CAP_RATIO).toFixed(1)}px ` +
            `cap height — the wordmark will read smaller than body text. Use >=38, or <KarrMark>.`,
        );
    }

    const width = Math.round(height * LOCKUP_RATIO);
    // Explicit width/height on both the wrapper and the images reserves the box
    // before the PNG decodes, so the header doesn't reflow on first paint.
    const box = { width, height };

    return (
        <span className={`inline-flex shrink-0 items-center ${className}`} style={box}>
            <img
                src="/karr-logo-light.png"
                alt="Karr"
                width={width}
                height={height}
                style={box}
                className="dark:hidden"
            />
            <img
                src="/karr-logo-dark.png"
                alt=""
                aria-hidden="true"
                width={width}
                height={height}
                style={box}
                className="hidden dark:block"
            />
        </span>
    );
});

export interface KarrMarkProps {
    /** Rendered edge in px — the mark is square. */
    size?: number;
    className?: string;
}

/**
 * The mark alone (teal disc, knocked-out monogram). Reads on any surface, so
 * there is no light/dark pair. Use where there is no room for the wordmark.
 */
export const KarrMark = memo(function KarrMark({ size = 32, className = '' }: KarrMarkProps) {
    return (
        <img
            src="/pwa-192x192.png"
            alt="Karr"
            width={size}
            height={size}
            style={{ width: size, height: size }}
            className={`shrink-0 ${className}`}
        />
    );
});
